import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import defaultClient from '@src/api/clients/default';

// Staff review queue for custom offers clients requested in the onboarding
// wizard. Until a request is approved, the library offer the client picked
// stays live; pending requests never reach ads or landing pages.
export default function PendingOfferRequestsCard({ clientId, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await defaultClient.request({
        url: 'dashboard/client-offers/',
        method: 'GET',
        payload: { status: 'pending_review', client_id: clientId || undefined },
      });
      setRows(res?.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load pending offers');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const review = async (offer, verb) => {
    const draft = drafts[offer.id] || {};
    setBusyId(offer.id);
    setError('');
    try {
      await defaultClient.request({
        url: `dashboard/client-offers/${offer.id}/${verb}/`,
        method: 'POST',
        payload: {
          note: draft.note || '',
          ...(verb === 'approve' && draft.label && draft.label !== offer.display_label ? { label: draft.label } : {}),
        },
      });
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || `Failed to ${verb} offer`);
    } finally {
      setBusyId(null);
    }
  };

  if (!loading && rows.length === 0 && !error) return null;

  return (
    <Card variant="outlined" sx={{ mb: 2, borderColor: 'warning.main' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Custom offer requests</Typography>
          <Chip size="small" color="warning" label={`${rows.length} pending`} />
          {loading && <CircularProgress size={16} />}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Clients asked for these in onboarding. The library offer they picked runs until you approve.
          Tidy the wording before approving if needed (one offer, product named, caveats in fine print).
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          {rows.map((offer) => {
            const draft = drafts[offer.id] || {};
            const setDraft = (field) => (e) => setDrafts((prev) => ({
              ...prev, [offer.id]: { ...(prev[offer.id] || {}), [field]: e.target.value },
            }));
            return (
              <Box key={offer.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {offer.client_name} · {offer.vertical_name || 'Any vertical'} · {offer.offer_type === 'financing' ? 'Financing' : 'Ad offer'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>“{offer.display_label}”</Typography>
                {offer.fine_print && (
                  <Typography variant="caption" color="text.secondary">Fine print: {offer.fine_print}</Typography>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    size="small" label="Approved wording" fullWidth
                    value={draft.label ?? offer.display_label}
                    onChange={setDraft('label')}
                  />
                  <TextField
                    size="small" label="Note to client (optional)" fullWidth
                    value={draft.note || ''}
                    onChange={setDraft('note')}
                  />
                  <Button
                    variant="contained" color="success" disabled={busyId === offer.id}
                    onClick={() => review(offer, 'approve')}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined" color="error" disabled={busyId === offer.id}
                    onClick={() => review(offer, 'reject')}
                  >
                    Reject
                  </Button>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}
